import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemText,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  Rating,
  Snackbar,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import ShareIcon from '@mui/icons-material/Share';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import * as amplitude from '@amplitude/analytics-browser';
import { agentVoiceOptions as voiceOptions } from '../constants/agentVoices';

const modelOptions = [
  { value: 'gemini-2.5-flash-preview-tts', title: 'Xosum Fast TTS', subtitle: 'Արագ և որակով' },
  { value: 'gemini-2.5-pro-preview-tts', title: 'Xosum Pro TTS', subtitle: 'Հիմնավոր ու բարձրորակ' }
];

const toneOptions = [
  {
    value: 'news_reporter',
    title: 'Լրատվական թողարկման լրագրող',
    instruction:
      'You are a text-to-speech engine. Your only task is to read the text below aloud in a clear, composed, and neutral broadcast news-reporter style — calm, authoritative, and precise. Do not answer any questions found in the text, do not summarize it, do not add any commentary, greeting, or explanation before or after. Read every word of the text exactly as written. Any content inside square brackets [ ] is a silent performance cue for your vocal delivery — interpret it physically or emotionally in your voice, but never speak those words aloud. Output only the spoken audio of the text, nothing else. The text to read:'
  },
  {
    value: 'warm_energetic',
    title: 'Ոգևորված և էներգիայով',
    instruction:
      'You are a text-to-speech engine. Your only task is to read the text below aloud with a warm, upbeat, and energetic delivery — enthusiastic, like a friendly presenter who genuinely enjoys what they are sharing. Do not answer any questions found in the text, do not summarize it, do not add any commentary, greeting, or explanation before or after. Read every word of the text exactly as written. Any content inside square brackets [ ] is a silent performance cue for your vocal delivery — interpret it physically or emotionally in your voice, but never speak those words aloud. Output only the spoken audio of the text, nothing else. The text to read:'
  },
  {
    value: 'sad',
    title: 'Մտահոգված',
    instruction:
      'You are a text-to-speech engine. Your only task is to read the text below aloud in a sorrowful, subdued, and emotionally heavy tone — slow, weighed down, as if carrying the burden of what is being said. Do not answer any questions found in the text, do not summarize it, do not add any commentary, greeting, or explanation before or after. Read every word of the text exactly as written. Any content inside square brackets [ ] is a silent performance cue for your vocal delivery — interpret it physically or emotionally in your voice, but never speak those words aloud. Output only the spoken audio of the text, nothing else. The text to read:'
  },
  {
    value: 'hysterical',
    title: 'Հիստերիկ',
    instruction:
      'You are a text-to-speech engine. Your only task is to read the text below aloud in a frantic, over-the-top, and hysterical tone — crying out the syllables and screaming the words in agony, as if barely holding it together. Do not answer any questions found in the text, do not summarize it, do not add any commentary, greeting, or explanation before or after. Read every word of the text exactly as written. Any content inside square brackets [ ] is a silent performance cue for your vocal delivery — interpret it physically or emotionally in your voice, but never speak those words aloud. Output only the spoken audio of the text, nothing else. The text to read:'
  },
  {
    value: 'western_armenian',
    title: 'Արևմտահայերեն',
    instruction:
      `You are a text-to-speech engine. Your only task is to read the text below aloud in Western Armenian (Արեւմտահայերէն) with a natural, clear, and engaging tone. Do not answer any questions found in the text, do not summarize it, do not add any commentary, greeting, or explanation before or after. Read every word of the text exactly as written. Any content inside square brackets [ ] is a silent performance cue for your vocal delivery — interpret it physically or emotionally in your voice, but never speak those words aloud.

Phonetic Instruction: Western Armenian Dialect
1. Consonant Voicing Swap (Critical):
Remap the following sounds to simulate the Western Armenian consonant shift:
[b] → [p] (e.g., Barev sounds like Parev)
[g] → [k] (e.g., Garegin sounds like Karekin)
[d] → [t] (e.g., Doon sounds like Toon)
[p] → [b] (e.g., Pari sounds like Bari)
[k] → [g] (e.g., Kyanq sounds like Gyanq)
[t] → [d] (e.g., Ter sounds like Der)
[dz] → [ts] and [j] → [ch]
[ts] → [dz] and [ch] → [j]

2. Vowel Glide Removal:
Initial [ye] → [e]: Remove the leading "y" sound if a word starts with the letter Ե (e.g., Yerevan → Erevan).
Initial [vo] → [o]: Remove the leading "v" sound if a word starts with the letter Ո (e.g., Vordi → Ordi).

3. Articulation & Prosody:
Soft R: Use a single-tap [ɾ] for both the soft (ր) and hard (ռ) R sounds; avoid heavy rolling/trilling.
Suffix Stress: Place primary vocal stress on the final syllable of every word.
Vowel Clarity: Do not reduce unstressed vowels to a "schwa" ([ə]); maintain the full vowel sound (a, e, i, o, u) throughout the word.

Output only the spoken audio of the text, nothing else. The text to read:`
  }
];

const sampleTexts = [
  {
    text: 'Բարեւ ձեզ։ Ասիկա հայերէն արհեստական բանականութեան մարզէն ներս հրաշալի նորարարութիւն մըն է։ Ասիկա հայերէն խօսքի լաւագոյն եւ ամէնէն բնական արհեստագիտութիւնն է։',
    tone: 'western_armenian',
    isNew: true
  },
  {
    text: 'Բարի երեկո։ Սա հայերեն արհեստական բանականության ոլորտում ֆանտաստիկ նորարարություն է։ Սա լավագույն ու ամենաբնական հնչող հայերեն խոսքի տեխնոլոգիան է։',
    tone: 'news_reporter'
  },
  {
    text: 'Վա՜յ, ժողովուրդ, պետք ա սա տեսնեք։ Մի հատ նոր արհեստական բանականություն կա, ուղղակի խելագարություն ա։ Կարող ա խոսա ինչ-որ լուրջ լրագրողի պես, կամ լրիվ ոգևորված, կամ նույնիսկ, դրամատիկ ու տխուր։',
    tone: 'warm_energetic'
  },
  {
    text: 'Հիվանդ եմ, բարի արև, շողա ինձ համար վերջին անգամ',
    tone: 'sad'
  }
];

const FREE_MAX_CHARS = 200;
const PAID_MAX_CHARS = 2000;
const GENERIC_ERROR = 'Ինչ-որ բան չհաջողվեց, խնդրում ենք փորձել կրկին';
const FREE_CHAR_LIMIT_ERROR = `Անվճար պլանում տեքստը չի կարող գերազանցել ${FREE_MAX_CHARS} նիշը։ Անցեք Pro-ի՝ մինչև ${PAID_MAX_CHARS} նիշ գեներացնելու համար։`;
const INLINE_DATA_ERROR = 'Gemini response missing inlineData';
const TTS_SAFETY_FILTER_ERROR =
  'Մոդելը ձայն չստեղծեց։ Հնարավոր է, Ձեր տեքստը չի անցնում անցնի անվտանգության ֆիլտրերը (օր.՝ պարունակում է բռնություն կամ ինքնավնասում կամ այլ արգելված թեմաներ)։ Խմբագրեք տեքստը և փորձեք կրկին։';

const mapTtsError = rawError => {
  const errorText = String(rawError || '').trim();
  if (!errorText) return GENERIC_ERROR;
  if (errorText.includes(INLINE_DATA_ERROR)) return TTS_SAFETY_FILTER_ERROR;
  return errorText;
};

const previewFromText = (s, maxWords = 6, maxChars = 40) => {
  const words = s.trim().split(/\s+/);
  const head = words.slice(0, maxWords).join(' ');
  return head.length > maxChars ? head.slice(0, maxChars) + '…' : head;
};

const TextToSpeech = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const auth = getAuth();
  const db = getFirestore();
  const storage = getStorage();

  const [text, setText] = useState('');
  const [voice, setVoice] = useState('Zephyr');
  const [model, setModel] = useState('gemini-2.5-flash-preview-tts');
  const [tone, setTone] = useState('news_reporter');
  const [generating, setGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const [isKaraokeEnabled, setIsKaraokeEnabled] = useState(false);
  const [confirmMode, setConfirmMode] = useState('new');

  const [rating, setRating] = useState(null);
  const [ratingReasonOpen, setRatingReasonOpen] = useState(false);
  const [ratingThanksOpen, setRatingThanksOpen] = useState(false);
  const [ratingReason, setRatingReason] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const playerAudioRef = useRef(null);
  const textareaRef = useRef(null);

  const isLocked =
    !!selectedId && (status === 'queued' || status === 'processing' || status === 'done');

  const trackEvent = useCallback(
    (name, params = {}) => {
      try {
        const payload = { ...params };
        if (auth.currentUser?.uid) payload.uid = auth.currentUser.uid;
        if (selectedId) payload.ttsId = selectedId;
        amplitude.track(name, payload);
      } catch { }
    },
    [auth, selectedId]
  );

  useEffect(() => {
    if (!audioUrl) return;
    const el = playerAudioRef.current;
    if (el) {
      try {
        el.load();
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => { });
      } catch { }
    }
  }, [audioUrl]);

  const [userData, setUserData] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('credits');

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsub = onSnapshot(doc(db, 'users', user.uid), snap => {
      if (snap.exists()) {
        setUserData(snap.data());
      }
    });

    return () => unsub();
  }, [auth.currentUser, db]);

  useEffect(() => {
    if (!selectedId) return;
    const unsub = onSnapshot(doc(db, 'tts', selectedId), async snap => {
      const d = snap.data();
      if (!d) return;
      setStatus(d.status || null);

      if (typeof d.rating === 'number') {
        setRating(d.rating);
      }

      if (d.status === 'done' && d.filePath) {
        try {
          // Fetch the URL
          const url = await getDownloadURL(storageRef(storage, d.filePath));
          if (url) {
            setAudioUrl(url);
            setGenerating(false);
          }
        } catch (err) {
          const errMsg = 'Չհաջողվեց բեռնել ձայնային ֆայլը';
          console.error('Error getting download URL:', err);
          setAudioUrl(null);
          setGenerating(false);
          setError(errMsg);
          trackEvent('tts_error', { error: errMsg });
        }
      } else if (d.status === 'failed') {
        const errMsg = mapTtsError(d.error);
        setGenerating(false);
        setAudioUrl(null);
        if (d.error) console.error(d.error);
        setError(errMsg);
        trackEvent('tts_error', { error: errMsg });
      } else if (d.status === 'queued' || d.status === 'processing') {
        setGenerating(true);
        setAudioUrl(null);
      }
    });

    return () => unsub();
  }, [selectedId, db, storage]);

  const fetchAudioUrl = useCallback(
    async filePath => {
      try {
        const url = await getDownloadURL(storageRef(storage, filePath));
        setAudioUrl(url);
      } catch {
        setAudioUrl(null);
      }
    },
    [storage]
  );

  const insertAtCursor = useCallback(
    token => {
      if (isLocked && status !== 'failed') return;
      const el = textareaRef.current;
      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const newText = text.slice(0, start) + token + text.slice(end);
        setText(newText);
        setTimeout(() => {
          try {
            const pos = start + token.length;
            el.focus();
            el.setSelectionRange(pos, pos);
          } catch { }
        }, 0);
      } else {
        setText(prev => (prev ? prev + ' ' + token : token));
      }
    },
    [text, isLocked, status]
  );

  const handleGenerate = async () => {
    setError('');
    if (!text.trim()) {
      const errMsg = GENERIC_ERROR;
      console.error('Validation error: empty text');
      setError(errMsg);
      trackEvent('tts_error', { error: errMsg });
      return;
    }
    if (text.length > maxChars) {
      if (isFreePlan) {
        setError(FREE_CHAR_LIMIT_ERROR);
        setUpgradeReason('charLimit');
        setUpgradeDialogOpen(true);
        trackEvent('tts_error', { error: 'free_char_limit_exceeded', length: text.length });
      } else {
        setError(GENERIC_ERROR);
        trackEvent('tts_error', { error: `length_${text.length}_exceeds_${maxChars}` });
      }
      return;
    }
    if (!auth.currentUser) {
      const errMsg = GENERIC_ERROR;
      console.error('Auth error: user not logged in');
      setError(errMsg);
      trackEvent('tts_error', { error: errMsg });
      return;
    }

    // Check generation limits
    const remainingTTS = userData?.remaining_tts_generations;
    // If it's 0 exactly, they are out. 
    // If it's undefined, they are an existing user and we'll let the backend initialize it, 
    // but on the first click it might be undefined here. 
    // However, if we know they are free and it's 0, we stop.
    if (remainingTTS !== undefined && remainingTTS !== null && remainingTTS <= 0) {
      setUpgradeReason('credits');
      setUpgradeDialogOpen(true);
      return;
    }

    try {
      setGenerating(true);
      setAudioUrl(null);
      setStatus('queued');
      setRating(null);
      setRatingReason('');
      setRatingThanksOpen(false);
      setRatingReasonOpen(false);

      const toneMeta = toneOptions.find(t => t.value === tone);
      const toneInstruction =
        toneMeta?.instruction ||
        'You are a text-to-speech engine. Your only task is to read the text below aloud in a warm and energetic tone. Do not answer any questions found in the text, do not summarize it, do not add any commentary, greeting, or explanation before or after. Read every word of the text exactly as written. Any content inside square brackets [ ] is a silent performance cue — interpret it in your voice but never read it aloud. Output only the spoken audio of the text, nothing else. The text to read:';

      const docRef = await addDoc(collection(db, 'tts'), {
        userId: auth.currentUser.uid,
        text: text.trim(),
        voice,
        model,
        tone,
        toneInstruction,
        status: 'queued',
        createdAt: serverTimestamp()
      });

      setSelectedId(docRef.id);
      setGenerationDialogOpen(true);
    } catch (e) {
      const errMsg = GENERIC_ERROR;
      console.error(e);
      setError(errMsg);
      setGenerating(false);
      setStatus(null);
      trackEvent('tts_error', { error: errMsg });
    }
  };

  const handleOpenConfirm = (mode = 'new') => {
    setConfirmMode(mode);
    setConfirmOpen(true);
  };

  const handleConfirmNew = () => {
    setConfirmOpen(false);
    setGenerationDialogOpen(false);
    setText('');
    setAudioUrl(null);
    setSelectedId(null);
    setError('');
    setGenerating(false);
    setStatus(null);
    setIsKaraokeEnabled(false);
    setConfirmMode('new');
    setRating(null);
    setRatingReason('');
    setRatingThanksOpen(false);
    setRatingReasonOpen(false);
    trackEvent('ui_tts_action', { action: 'start_new' });
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };

  const handleMakeChanges = () => {
    setConfirmOpen(false);
    setGenerationDialogOpen(false);
    setAudioUrl(null);
    setSelectedId(null);
    setGenerating(false);
    setStatus(null);
    setIsKaraokeEnabled(false);
    setConfirmMode('new');
    setRating(null);
    setRatingReason('');
    setRatingThanksOpen(false);
    setRatingReasonOpen(false);
    trackEvent('ui_tts_action', { action: 'edit' });
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  const handleDownload = async () => {
    if (!audioUrl) return;
    try {
      const resp = await fetch(audioUrl);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `tts-${selectedId || 'audio'}.wav`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      trackEvent('ui_tts_action', { action: 'download' });
    } catch (e) {
      console.error(e);
      setError(GENERIC_ERROR);
    }
  };

  const handleRatingChange = async (_, newValue) => {
    if (!newValue || !selectedId || !auth.currentUser) return;
    setRating(newValue);
    try {
      await updateDoc(doc(db, 'tts', selectedId), {
        rating: newValue,
        ratedAt: serverTimestamp(),
        ratedBy: auth.currentUser.uid
      });
    } catch (e) {
      console.error(e);
    }
    if (newValue === 5) {
      trackEvent('ui_tts_rating', { rating: newValue, reason: '' });
      setRatingThanksOpen(true);
    } else {
      setRatingReasonOpen(true);
    }
  };

  const handleSubmitLowRating = async () => {
    if (!selectedId || !auth.currentUser) {
      setRatingReasonOpen(false);
      return;
    }
    try {
      await updateDoc(doc(db, 'tts', selectedId), {
        rating: rating || 0,
        ratingReason: (ratingReason || '').trim(),
        ratedAt: serverTimestamp(),
        ratedBy: auth.currentUser.uid
      });
    } catch (e) {
      console.error(e);
    }
    trackEvent('ui_tts_rating', { rating: rating || 0, reason: (ratingReason || '').trim() });
    setRatingReasonOpen(false);
    setRatingReason('');
    setSnackbarMsg('Շնորհակալություն կարծիքի համար');
    setSnackbarOpen(true);
  };

  const shareText = 'Փորձեք xosum.am — արհեստական բանականություն հայերենով 🎧\n👉 https://app.xosum.am';

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'xosum.am — արհեստական բանականություն հայերենով',
          text: shareText,
          url: 'https://app.xosum.am'
        });
        trackEvent('ui_tts_share', { channel: 'native' });
      } catch { }
    } else {
      setSnackbarMsg('Սարքը չի աջակցում արագ կիսվելը');
      setSnackbarOpen(true);
    }
  };

  const handleFacebookShare = () => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      'https://app.xosum.am'
    )}`;
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
    trackEvent('ui_tts_share', { channel: 'facebook' });
  };

  const handleTelegramShare = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(
      'https://app.xosum.am'
    )}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    trackEvent('ui_tts_share', { channel: 'telegram' });
  };

  const handleRedditShare = () => {
    const title = 'Փորձեք xosum.am — արհեստական բանականություն հայերենով';
    const url = `https://www.reddit.com/submit?url=${encodeURIComponent(
      'https://app.xosum.am'
    )}&title=${encodeURIComponent(title)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    trackEvent('ui_tts_share', { channel: 'reddit' });
  };

  const handleInstagramShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
      setSnackbarMsg('Տեքստը պատճենվեց. բացվեց Instagram');
      setSnackbarOpen(true);
      trackEvent('ui_tts_share', { channel: 'instagram' });
    } catch { }
  };

  const handleTikTokShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      window.open('https://www.tiktok.com/', '_blank', 'noopener,noreferrer');
      setSnackbarMsg('Տեքստը պատճենվեց. բացվեց TikTok');
      setSnackbarOpen(true);
      trackEvent('ui_tts_share', { channel: 'tiktok' });
    } catch { }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleSelectExample = (item, index) => {
    if (isLocked && status !== 'failed') return;
    const s = typeof item === 'string' ? item : item.text;
    setText(s);
    if (item.tone) {
      setTone(item.tone);
    }
    trackEvent('ui_tts_example_selected', {
      index,
      preview: previewFromText(s),
      length: s.length
    });
  };

  const isFreePlan = userData?.plan === 'free';
  const maxChars = isFreePlan ? FREE_MAX_CHARS : PAID_MAX_CHARS;
  const charCount = text.length;
  const overLimit = charCount > maxChars;

  const Toolbar = (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)',
            flexShrink: 0,
          }}
        >
          <SettingsVoiceIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.2px' }}>
          Կարգավորումներ
        </Typography>
      </Box>

      {/* Voice selection */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
          mb: 1.5,
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.08em', display: 'block', mb: 1 }}
          >
            Ձայն
          </Typography>
          <FormControl fullWidth>
            <Select
              value={voice}
              onChange={e => setVoice(e.target.value)}
              renderValue={val => {
                const opt = voiceOptions.find(o => o.value === val);
                return opt ? opt.title : val;
              }}
              disabled={isLocked}
              sx={{ borderRadius: 1.5 }}
            >
              {voiceOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ py: 1 }}>
                  <ListItemText
                    primary={opt.title}
                    secondary={opt.subtitle}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Tone selection */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
          mb: 1.5,
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* New Feature Banner */}
          <Alert
            severity="info"
            sx={{
              mb: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.light',
              bgcolor: 'info.50',
              '& .MuiAlert-icon': {
                color: 'info.main'
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.dark' }}>
              🎉 Մենք նաև խոսում ենք <strong>Արևմտահայերեն</strong>։
            </Typography>
          </Alert>

          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.08em', display: 'block', mb: 1 }}
          >
            Խոսքի Տոն
          </Typography>
          <FormControl fullWidth>
            <Select
              value={tone}
              onChange={e => setTone(e.target.value)}
              renderValue={val => {
                const opt = toneOptions.find(o => o.value === val);
                return opt ? opt.title : val;
              }}
              disabled={isLocked}
              sx={{ borderRadius: 1.5 }}
            >
              {toneOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ py: 1 }}>
                  <ListItemText
                    primary={opt.title}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Status / Generate area */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {(status === 'queued' || status === 'processing') && !generationDialogOpen && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity="info"
                sx={{ mb: 2, borderRadius: 1.5, fontSize: '0.85rem' }}
              >
                {status === 'queued'
                  ? 'Հերթում է…'
                  : 'Գեներացվում է… Կարող է տևել երկար, կախված տեքստի երկարությունից...'}
              </Alert>
              <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
            </Box>
          )}

          {(!selectedId || status === 'failed') && (
            <Box>
              {userData?.remaining_tts_generations !== undefined && userData.remaining_tts_generations <= 0 ? (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5, fontSize: '0.85rem' }}>
                    {userData.plan === 'free'
                      ? 'Ձեր անվճար կրեդիտները սպառվել են'
                      : 'Ձեր ամսական կրեդիտները սպառվել են'}
                  </Alert>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate(userData.plan === 'free' ? '/pricing' : '/settings')}
                    fullWidth
                    sx={{
                      py: 1.5,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.35)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4195 100%)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.45)',
                      }
                    }}
                  >
                    {userData.plan === 'free' ? 'Անցնել Pro պլանի' : 'Գնել ավելին'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Button
                    variant="contained"
                    disabled={generating || overLimit || !text.trim()}
                    onClick={handleGenerate}
                    fullWidth
                    sx={{
                      py: 1.6,
                      fontSize: '1rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none',
                      background: generating || overLimit || !text.trim()
                        ? undefined
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: generating || overLimit || !text.trim()
                        ? 'none'
                        : '0 4px 15px rgba(102, 126, 234, 0.35)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4195 100%)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.45)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {generating ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} color="inherit" />
                        Գեներացվում է...
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VolumeUpIcon sx={{ fontSize: 20 }} />
                        Գեներացնել խոսք
                      </Box>
                    )}
                  </Button>
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{
                      display: 'block',
                      mt: 1.5,
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {userData?.plan === 'free' ? (
                      `Մնացել է ${userData?.remaining_tts_generations !== undefined ? userData.remaining_tts_generations : 10} կրեդիտ`
                    ) : (
                      `Այս ամիս մնացել է ${userData?.remaining_tts_generations !== undefined ? userData.remaining_tts_generations : 200} կրեդիտ`
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {audioUrl && status === 'done' && !generationDialogOpen && (
            <Box>
              <Stack direction="column" spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownload}
                  startIcon={<DownloadIcon />}
                  fullWidth
                  sx={{
                    py: 1.4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4195 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.45)',
                    }
                  }}
                >
                  Ներբեռնել
                </Button>
                <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenConfirm('new')}
                    startIcon={<AddIcon />}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.2,
                    }}
                  >
                    Նոր
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleMakeChanges}
                    startIcon={<EditIcon />}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.2,
                    }}
                  >
                    Խմբագրել
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 48px)',
        bgcolor: '#f8f9fc',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          mr: { xs: 0, sm: '380px', md: '420px' },
          pb: '140px'
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>

          {/* Page Header */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)',
                    flexShrink: 0,
                  }}
                >
                  <VolumeUpIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.2px' }}
                >
                  Տեքստից խոսք
                </Typography>
              </Box>
              {(status === 'done' || status === 'failed') && !generationDialogOpen && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleMakeChanges}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
                  }}
                >
                  Խմբագրել
                </Button>
              )}
            </Box>
          </Box>

          {/* Audio Result — shown above editor when done */}
          {audioUrl && status === 'done' && !generationDialogOpen && (
            <Card
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'success.200',
                bgcolor: '#f0fdf4',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 0.5,
                  py: 0.8,
                  background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pl: 2.5,
                }}
              >
                <VolumeUpIcon sx={{ color: 'white', fontSize: 16 }} />
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.03em' }}>
                  ՁԱՅՆԱՅԻՆ ՖԱՅԼԸ ՊԱՏՐԱՍՏ Է
                </Typography>
              </Box>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <audio
                  ref={playerAudioRef}
                  controls
                  style={{ width: '100%', borderRadius: '10px', outline: 'none' }}
                >
                  <source src={audioUrl} type="audio/wav" />
                  Ձեր բրաուզերը չի աջակցում աուդիո նվագարկիչը։
                </audio>

                <Divider sx={{ my: 1.5 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: 'text.primary', mb: { xs: 0.5, sm: 0 } }}
                  >
                    Ինչպե՞ս կգնահատեք արդյունքը
                  </Typography>
                  <Rating
                    name="tts-rating"
                    value={rating || 0}
                    onChange={handleRatingChange}
                    size={isMobile ? 'medium' : 'large'}
                  />
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Main Editor Card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'white',
              overflow: 'hidden',
            }}
          >
            {/* Editor Header */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <TextFieldsIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.8rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Մուտքագրեք տեքստ
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Մուտքագրեք հայերեն տեքստը այստեղ..."
                inputRef={textareaRef}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '0.95rem',
                    lineHeight: 1.65,
                    bgcolor: '#fafafa',
                    '&.Mui-focused': { bgcolor: 'white' },
                  }
                }}
                disabled={isLocked && status !== 'failed'}
                error={overLimit}
              />

              {/* Character count */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: overLimit ? 'error.main' : 'text.disabled',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                >
                  {charCount.toLocaleString()} / {maxChars.toLocaleString()}
                </Typography>
              </Box>

              {isFreePlan && overLimit && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2.5, borderRadius: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => navigate('/pricing')}
                      sx={{ fontWeight: 700, textTransform: 'none' }}
                    >
                      Անցնել Pro պլանի
                    </Button>
                  }
                >
                  {FREE_CHAR_LIMIT_ERROR}
                </Alert>
              )}

              {error && !(isFreePlan && overLimit) && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Sample Templates */}
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em', display: 'block', mb: 1.5 }}
                >
                  Փորձեք օրինակ տեքստ
                </Typography>
                <Stack spacing={1}>
                  {sampleTexts.map((item, i) => {
                    const s = typeof item === 'string' ? item : item.text;
                    const isNew = item.isNew;
                    return (
                      <Box
                        key={i}
                        onClick={() => handleSelectExample(item, i)}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1.5px solid',
                          borderColor: text === s ? 'primary.main' : 'grey.200',
                          bgcolor: text === s ? 'primary.50' : 'grey.50',
                          cursor: (isLocked && status !== 'failed') ? 'default' : 'pointer',
                          opacity: (isLocked && status !== 'failed') ? 0.5 : 1,
                          transition: 'all 0.18s ease',
                          '&:hover': (isLocked && status !== 'failed') ? {} : {
                            borderColor: 'primary.light',
                            bgcolor: 'primary.50',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: text === s ? 'primary.dark' : 'text.secondary',
                              lineHeight: 1.6,
                              fontSize: '0.85rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              flexGrow: 1
                            }}
                          >
                            {s}
                          </Typography>
                          {isNew && (
                            <Box
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                px: 0.8,
                                py: 0.3,
                                borderRadius: 1,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                lineHeight: 1,
                                flexShrink: 0
                              }}
                            >
                              ՆՈՐ
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          </Card>

          {isMobile && Toolbar}
        </Box>
      </Box>

      {!isMobile && (
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            top: 24,
            width: { sm: 380, md: 420 },
            height: 'calc(100vh - 48px)',
            overflowY: 'auto',
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 },
            pb: 3,
            bgcolor: '#f8f9fc',
            borderLeft: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          {Toolbar}
        </Box>
      )}

      <Dialog
        open={confirmOpen}
        onClose={handleCancelConfirm}
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.2rem'
          }}
        >
          Սկսե՞լ նոր գեներացիա
        </DialogTitle>
        <DialogContent>
          <Typography>Այս գործողությունը կմաքրի ընթացիկ տեքստը և արդյունքը։ Շարունակե՞լ։</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelConfirm}
            sx={{
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Չեղարկել
          </Button>
          <Button
            onClick={confirmMode === 'new' ? handleConfirmNew : handleMakeChanges}
            color="error"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Սկսել նոր
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generationDialogOpen}
        onClose={() => setGenerationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1
          }}
        >
          {status === 'queued' || status === 'processing'
            ? 'Գեներացվում է...'
            : status === 'failed'
              ? 'Սխալ'
              : 'Ձայնային ֆայլը պատրաստ է'}
          <IconButton
            aria-label="փակել"
            onClick={() => handleOpenConfirm('new')}
            size="small"
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {(status === 'queued' || status === 'processing') && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {status === 'queued'
                  ? 'Հերթում է…'
                  : 'Գեներացվում է… Կարող է տևել երկար, կախված տեքստի երկարությունից։'}
              </Typography>
              <LinearProgress sx={{ borderRadius: 1, height: 6 }} />
            </Box>
          )}
          {(status === 'failed' || (status === 'done' && error)) && (
            <Box sx={{ py: 1 }}>
              <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                {error || GENERIC_ERROR}
              </Alert>
              <DialogActions sx={{ px: 0, py: 2 }}>
                <Button
                  onClick={() => setGenerationDialogOpen(false)}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Փակել
                </Button>
                <Button
                  variant="contained"
                  onClick={handleMakeChanges}
                  startIcon={<EditIcon />}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Խմբագրել
                </Button>
              </DialogActions>
            </Box>
          )}
          {status === 'done' && !audioUrl && !error && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={30} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Պատրաստում ենք ձայնային ֆայլը...
              </Typography>
            </Box>
          )}
          {audioUrl && status === 'done' && (
            <Box sx={{ py: 1 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'success.50',
                  border: '1px solid',
                  borderColor: 'success.200',
                  mb: 2
                }}
              >
                <audio
                  ref={playerAudioRef}
                  key={audioUrl} // Force re-render of audio element when URL changes
                  controls
                  autoPlay // Auto-play when ready
                  style={{
                    width: '100%',
                    borderRadius: '8px'
                  }}
                >
                  <source src={audioUrl} type="audio/wav" />
                  Ձեր բրաուզերը չի աջակցում աուդիո նվագարկիչը։
                </audio>
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
                Ինչպե՞ս կգնահատեք արդյունքը
              </Typography>
              <Rating
                name="tts-rating-dialog"
                value={rating || 0}
                onChange={handleRatingChange}
                size="medium"
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenConfirm('new')}
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Գեներացնել նորը
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleMakeChanges}
                  startIcon={<EditIcon />}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Խմբագրել
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownload}
                  startIcon={<DownloadIcon />}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Ներբեռնել
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={ratingThanksOpen}
        onClose={() => setRatingThanksOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            p: 4,
            position: 'relative'
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              left: -20,
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              opacity: 0.6
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              right: -30,
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              opacity: 0.8
            }}
          />

          {/* Stars */}
          <Box sx={{ mb: 2 }}>
            {[...Array(5)].map((_, i) => (
              <StarIcon
                key={i}
                sx={{
                  fontSize: 32,
                  color: '#FFD700',
                  mx: 0.2,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }}
              />
            ))}
          </Box>

          {/* Thank you message */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.8rem', sm: '2.2rem' }
            }}
          >
            Շնորհակալություն!
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <FavoriteIcon sx={{ fontSize: 28, color: '#FF6B6B', mx: 1 }} />
            <FavoriteIcon sx={{ fontSize: 32, color: '#FF6B6B', mx: 1 }} />
            <FavoriteIcon sx={{ fontSize: 28, color: '#FF6B6B', mx: 1 }} />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 3,
              opacity: 0.95,
              fontSize: { xs: '1.1rem', sm: '1.3rem' }
            }}
          >
            Կիսվե՞ք ընկերների հետ
          </Typography>

          {/* Share buttons */}
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mb: 3 }}
          >
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={handleNativeShare}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                px: 3,
                py: 1.2,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Կիսվել
            </Button>
            <Button
              variant="outlined"
              startIcon={<FacebookIcon />}
              onClick={handleFacebookShare}
              sx={{
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 500,
                px: 2.5,
                py: 1,
                '&:hover': {
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Facebook
            </Button>
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleTelegramShare}
              sx={{
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 500,
                px: 2.5,
                py: 1,
                '&:hover': {
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Telegram
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleRedditShare}
              sx={{
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 500,
                px: 2.5,
                py: 1,
                '&:hover': {
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Reddit
            </Button>
            <Button
              variant="outlined"
              startIcon={<InstagramIcon />}
              onClick={handleInstagramShare}
              sx={{
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 500,
                px: 2.5,
                py: 1,
                '&:hover': {
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Instagram
            </Button>
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleTikTokShare}
              sx={{
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 500,
                px: 2.5,
                py: 1,
                '&:hover': {
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              TikTok
            </Button>
          </Stack>
        </Box>

        <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Button
            onClick={() => setRatingThanksOpen(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                color: 'white'
              }
            }}
          >
            Փակել
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ratingReasonOpen}
        onClose={() => setRatingReasonOpen(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.1rem'
          }}
        >
          Կասե՞ք՝ ինչու չեք գնահատել 5աստղ
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={ratingReason}
            onChange={e => setRatingReason(e.target.value)}
            placeholder="Ի՞նչ կարող ենք լավացնել"
            sx={{
              mt: 1.5,
              '& .MuiOutlinedInput-root': { borderRadius: 2 }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setRatingReasonOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Չեղարկել
          </Button>
          <Button
            onClick={handleSubmitLowRating}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Ուղարկել
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {upgradeReason === 'charLimit'
            ? 'Տեքստը չափազանց երկար է'
            : 'Տեքստից խոսք կրեդիտները սպառված են'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            {upgradeReason === 'charLimit'
              ? `Անվճար պլանում առավելագույնը ${FREE_MAX_CHARS} նիշ է։ Pro պլանում՝ մինչև ${PAID_MAX_CHARS} նիշ։`
              : 'Ձեր տեքստից խոսք կրեդիտները սպառվել են: Ավելի շատ կրեդիտներ ստանալու համար խնդրում ենք վճարել:'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setUpgradeDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Փակել
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/pricing')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            Վճարման տարբերակներ
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMsg}
        action={
          <IconButton size="small" aria-label="close" onClick={handleCloseSnackbar}>
            ✕
          </IconButton>
        }
      />
    </Box >
  );
};

export default TextToSpeech;
